import React, { useState, useEffect, useRef } from 'react';
// Components
import CallToActionOne from '../cta/CallToActionOne';
import FollowMeOnCta from '../socialMedia/FollowMeOnCta';
import SocialMediaCtaComponent from '../socialMedia/SocialMediaCtaComponent';
// Data
import { videoDataArray } from '../../utils/data/VideoData';
import { fanSitesArray } from '../../utils/data/FansiteData';
// Icons
import { Link } from 'react-router-dom';
// Constants
import { CONTACT_PAGE_URL } from '../../utils/constants/Routes';
import { OUTFIT_REQUEST_AMOUNT, PER_MIN_AMOUNT } from '../../utils/constants/Constants';

function ClipsPageMainContainer() {
  const [thumbnails, setThumbnails] = useState([]);
  const [showVideos, setShowVideos] = useState({});
  const videoRefs = useRef({});

  // Generate thumbnails from videos
  useEffect(() => {
    videoDataArray.forEach((clip, index) => {
      const video = document.createElement('video');
      video.src = clip.videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';

      video.addEventListener('loadeddata', () => {
        video.currentTime = 1;
      });

      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL('image/jpeg');
        setThumbnails((prev) => {
          const updated = [...prev];
          updated[index] = thumbnail;
          return updated;
        });
      });
    });
  }, []);

  // Autoplay video on reveal
  useEffect(() => {
    Object.entries(showVideos).forEach(([index, visible]) => {
      if (visible && videoRefs.current[index]) {
        const videoElement = videoRefs.current[index];
        // Ensure it's loaded before trying to play
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.warn(`Video ${index} couldn't autoplay:`, err);
          });
        }
      }
    });
  }, [showVideos]);

  const handleClick = (index) => {
    setShowVideos((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <main role='main' className='sm:pt-4 lg:pt-12'>
      {/* Videos */}
      <section className='grid container h-fit mx-auto mt-8 mb-8 py-6 px-8 lg:px-16'>
        <div className='grid gap-8 md:grid-cols-2'>
          {videoDataArray.map((clip, index) => (
            <div
              key={index}
              className='grid h-fit lg:h-full rounded-xl overflow-hidden shadow-lg bg-colour1 dark:bg-colour2 text-colour2'
            >
              <video
                controls
                className='w-full object-cover'
                loading='lazy'
                src={`${clip.videoUrl}#t=0.001`}
              />
              <div className='p-4'>
                <h3 className='text-xl font-semibold mb-2'>{clip.title}</h3>
                <p className='text-sm'>{clip.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className='grid container gap-y-6 h-fit mx-auto mt-8 mb-8 py-6 px-8 lg:px-16'>
        {/* Details / Pricing */}
        <div className='grid rounded-xl border-2 border-solid border-colour7 bg-black/40 p-6 lg:p-8 shadow-cardShadow'>
          <div className='grid gap-4'>
            <h4 className='text-2xl font-titleFont text-center lg:text-left'>
              How to Order
            </h4>

            <div className='grid gap-2 text-base'>
              <div>
                • Message Me on <strong>Loyalfans</strong>,{' '}
                <strong>Twitter</strong>, or <strong>Fetlife</strong> with a
                description and desired length.
              </div>
              <div>
                • I’ll either agree, tweak to fit what I enjoy making, or deny.
              </div>
            </div>

            {/* Base Pricing */}
            <div className='grid gap-3 rounded-lg border-2 border-solid border-colour8/60 bg-black/30 p-4'>
              <h5 className='text-xl font-semibold'>Base Pricing</h5>
              <div className='grid gap-1'>
                <div>
                  • <strong>${PER_MIN_AMOUNT}/min</strong>
                </div>{' '}
                {/* added note */}
                <div>
                  • <strong>5 minute minimum</strong>
                </div>
                <div>
                  • Specific outfit requests:{' '}
                  <strong>$75 extra</strong>
                </div>{' '}
                {/* corrected from $100 */}
                <div>
                  • Expected delivery: <strong>4 weeks</strong> (no extra fees)
                </div>
              </div>
            </div>

            {/* Rush Options */}
            <div className='grid gap-3 rounded-lg border-2 border-solid border-colour8/60 bg-black/30 p-4'>
              <h5 className='text-xl font-semibold'>Rush Options</h5>
              <div className='grid gap-1'>
                <div>
                  • Within 3 weeks: <strong>+ $100</strong>
                </div>
                <div>
                  • Within 2 weeks: <strong>+ $500</strong>
                </div>
                <div>
                  • Within 1 week: <strong>+ $1000</strong>
                </div>
              </div>
            </div>

            {/* Add-ons (unchanged section, just confirming items match the post) */}
            <div className='grid gap-3 rounded-lg border-2 border-solid border-colour8/60 bg-black/30 p-4'>
              <h5 className='text-xl font-semibold'>Add-Ons</h5>
              <div className='grid gap-1'>
                <div>
                  • Name inclusion: <strong>+ $500</strong>
                </div>
                <div>
                  • Censored version: <strong>+ $500</strong>
                </div>
              </div>
            </div>

            {/* Also Available (left as you had it) */}
            <div className='grid gap-2'>
              <h5 className='text-xl font-semibold'>Also Available</h5>
              <div className='grid gap-1 text-base'>
                <div>
                  • Custom picture sets and audios — pricing varies, please
                  message for details.
                </div>
              </div>
            </div>

            {/* Nudity Disclaimer (wording updated to match the post) */}
            <div className='grid gap-2 rounded-lg border-2 border-solid border-colour8/60 bg-black/20 p-4 text-base italic'>
              • Nudity is <strong>NOT</strong> an option. Nipple pasties and
              panties <strong>ONLY</strong>. You do not deserve anything more.
            </div>
          </div>
        </div>

        <div>
          <Link
            to={CONTACT_PAGE_URL}
            className='grid place-items-center cursor-pointer hover:bg-colour8 border-4 shadow-cardShadow border-solid border-colour8 bg-black w-full h-fit px-10 md:px-12 py-3'
            aria-label='Order a custom clip'
          >
            Order a Custom Clip
          </Link>
        </div>
      </section>

      {/* Custom */}
      <section className='grid lg:container mt-16 lg:mt-24 px-8 lg:px-16 mx-auto'>
        <div className='grid gap-4 text-center'>
          <h2 className='text-3xl font-titleFont text-colour8 tracking-widest'>
            Kinks & Fetishes I Offer
          </h2>
          <p className='text-lg opacity-80 leading-relaxed'>
            Here’s the list of kinks and fetishes I offer for custom and premade
            clips.
          </p>
          <ul className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 text-lg mt-4 list-disc list-inside pl-4'>
            <li>Body / muscle / foot worship</li>
            <li>CEI / SPH / Humiliation / Degradation</li>
            <li>Chastity</li>
            <li>Cuck fantasy</li>
            <li>Denim / Leather fetish</li>
            <li>Femdom POV</li>
            <li>Financial Domination</li>
            <li>Gooning / Oil</li>
            <li>JOI</li>
            <li>Post workout worship / sweaty</li>
            <li>Power Exchange</li>
            <li>Slave training</li>
            <li>Sneaker / boot / heel / sock worship</li>
            <li>Spit</li>
            <li>Strap on worship / pegging</li>
            <li>Tease & Denial</li>
          </ul>

          <p className='text-lg opacity-80 leading-relaxed'>
            Nudity is limited to nipples. Nude clips with pixelation are also
            available for particularly pathetic losers.
          </p>
        </div>
      </section>

      {/* Clipstores Section */}
      <section className='grid lg:container mt-16 lg:mt-24 px-8 lg:px-16 mx-auto'>
        <section className='grid my-auto lg:order-last'>
          {/* Header */}
          <section className='grid grid-cols-reg w-full gap-x-6 pb-4'>
            {/* Large screen */}
            <div className='hidden md:grid items-center'>
              <h4 className='text-3xl font-titleFont text-colour8 tracking-widest'>
                Fansites
              </h4>
            </div>
            {/* Phone screen */}
            <div className='grid md:hidden items-center'>
              <h4 className='text-2xl font-titleFont text-colour8 tracking-widest'>
                Fansites
              </h4>
            </div>
            <div className='grid items-center'>
              <div
                className='h-[2px] -mt-2 bg-colour8'
                role='separator'
                aria-hidden='true'
              ></div>
            </div>
          </section>

          <section className='grid lg:grid-cols-2'>
            <div>
              <p className='text-lg text-center lg:text-left opacity-80 leading-relaxed'>
                My collection of elite BDSM clips — intense, alluring, and
                utterly unforgettable.
              </p>
              <div className='mx-auto grid justify-center lg:justify-start'>
                <ul className='list-none mt-4 space-y-3 px-4 text-center lg:text-left text-lg'>
                  <li>Female Domination Clips</li>
                  <li>Fetish & Power Play</li>
                </ul>
              </div>
            </div>
            <div className='py-6 grid md:grid-cols-3 gap-y-8'>
              {fanSitesArray.map((site, index) => (
                <a
                  key={index}
                  href={site.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='px-6 grid grid-rows-rev rounded-lg transition-transform transform hover:scale-105'
                >
                  {site.logo && !site.icon ? (
                    <img
                      src={site.logo}
                      alt={`${site.name} Logo`}
                      className='h-16 brightness-125 mx-auto'
                      loading='lazy'
                    />
                  ) : site.icon && site.logo ? (
                    <div className='flex items-center justify-center gap-3'>
                      {/* Check if site.icon is a string or a React icon */}
                      {typeof site.icon === 'string' ? (
                        <img
                          src={site.icon}
                          alt={`${site.name} Icon`}
                          className='h-10'
                          loading='lazy'
                        />
                      ) : (
                        <span className='text-4xl text-colour1'>
                          {site.icon}
                        </span>
                      )}
                      <img
                        src={site.logo}
                        alt={`${site.name} Logo`}
                        className='h-16 brightness-125'
                        loading='lazy'
                      />
                    </div>
                  ) : site.icon && site.textOnly ? (
                    // Special case: Telegram (Icon + Text)
                    <div className='grid w-fit gap-4 justify-center mx-auto grid-flow-col items-center'>
                      <div>
                        {/* Check if site.icon is a string or a React icon */}
                        {typeof site.icon === 'string' ? (
                          <img
                            src={site.icon}
                            alt={`${site.name} Icon`}
                            className='h-10'
                            loading='lazy'
                          />
                        ) : (
                          <span className='text-4xl text-colour1'>
                            {site.icon}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className='text-2xl lg:text-4xl uppercase text-center mt-2 text-colour1 font-semibold'>
                          {site.name}
                        </span>
                      </div>
                    </div>
                  ) : site.icon ? (
                    <div className='grid justify-center h-fit'>
                      {/* Check if site.icon is a string or a React icon */}
                      {typeof site.icon === 'string' ? (
                        <img
                          src={site.icon}
                          alt={`${site.name} Icon`}
                          className='h-10'
                          loading='lazy'
                        />
                      ) : (
                        <div className='grid items-center'>
                          <span className='text-4xl text-colour8'>
                            {site.icon}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='text-center text-colour1 uppercase text-4xl font-bold'>
                      {site.name}
                    </div>
                  )}
                  <div className='text-center pt-4'>
                    <p className='text-2xl font-titleFont text-colour1'>
                      {site.name}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </section>
        </section>
      </section>

      {/* Cta */}
      <CallToActionOne />

      {/* Follow me */}
      <div className='py-12'>
        <FollowMeOnCta />
      </div>

      {/* Social cta */}
      <SocialMediaCtaComponent />
    </main>
  );
}

export default ClipsPageMainContainer;
